import React, { FC, useEffect, useState } from "react";
import {
  LuCheck,
  LuChevronDown,
  LuLayoutList,
  LuPanelBottom,
  LuPanelRight,
} from "react-icons/lu";

import { READING_PANES, ReadingPane } from "./mailPage.utils";

const ICONS: Record<ReadingPane, React.ReactNode> = {
  right: <LuPanelRight size={16} />,
  bottom: <LuPanelBottom size={16} />,
  off: <LuLayoutList size={16} />,
};

/**
 * "Reading pane: Right / Bottom / Off" in the Mail header, like Outlook's
 * View > Reading pane (Roadmap 1840).
 */
export const ReadingPaneMenu: FC<{
  value: ReadingPane;
  onChange: (pane: ReadingPane) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = READING_PANES.find((p) => p.key === value);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span className={"relative"}>
      <button
        type={"button"}
        aria-haspopup={"menu"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={
          "inline-flex h-10 items-center gap-2 rounded-[9px] border border-[#DCE2EA] bg-white px-[13px] text-[13.5px] font-semibold text-[#3A4256] hover:border-[#B9A9FF] hover:text-[#6B4EFF]"
        }>
        {ICONS[value]}
        <span>
          Reading pane:{" "}
          <span className={"font-bold"}>{current?.label || "Right"}</span>
        </span>
        <LuChevronDown size={14} />
      </button>
      {open && (
        <>
          <span
            className={"fixed inset-0 z-30"}
            onClick={() => setOpen(false)}
          />
          <span
            role={"menu"}
            aria-label={"Reading pane"}
            className={
              "absolute right-0 top-[calc(100%+6px)] z-40 flex w-[280px] flex-col rounded-[11px] border border-[#ECEEF2] bg-white p-[6px] shadow-[0_14px_40px_rgba(20,22,34,0.22)]"
            }>
            <span
              className={
                "px-[11px] pb-1 pt-[6px] text-[11.5px] font-extrabold uppercase tracking-[0.5px] text-[#8A90A2]"
              }>
              Reading pane
            </span>
            {READING_PANES.map((p) => {
              const on = p.key === value;
              return (
                <button
                  type={"button"}
                  role={"menuitemradio"}
                  aria-checked={on}
                  key={p.key}
                  onClick={() => {
                    onChange(p.key);
                    setOpen(false);
                  }}
                  className={
                    "flex items-center gap-[10px] rounded-[8px] px-[11px] py-[8px] text-left hover:bg-[#F4F2FF]"
                  }
                  style={{ color: on ? "#6B4EFF" : "#3A4256" }}>
                  <span className={"flex-none"}>{ICONS[p.key]}</span>
                  <span className={"flex min-w-0 flex-1 flex-col"}>
                    <span className={"text-[13.5px] font-bold"}>{p.label}</span>
                    <span className={"text-[12px] text-[#8A90A2]"}>
                      {p.hint}
                    </span>
                  </span>
                  {on && <LuCheck size={16} className={"flex-none"} />}
                </button>
              );
            })}
          </span>
        </>
      )}
    </span>
  );
};
